import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bot,
  User,
  Send,
  X,
  ExternalLink,
  FileText,
  Info,
  ListPlus
} from "lucide-react";
import { fetchAiSearchParts } from "../../store/aiAssistant/aiAssistantAction";
import { addMessage } from "../../store/aiAssistant/aiAssitantSlice";
import { fetchSellerById } from "../../store/partChat/partChatActions";
import { fetchPartById } from "../../store/useParts/usePartsThunk";
import { useToast } from "@/components/ui/use-toast";

const AiAssistantChatDialog = ({
  isOpen,
  onClose,
  messages = [],
  isLoading
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const { aiResponse } = useSelector((state) => state.ai);
  const { user } = useSelector((state) => state.auth);
  const scrollAreaRef = useRef(null);
  const [query, setQuery] = useState();

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();

    dispatch(
      addMessage({
        sender: "user",
        type: "text",
        text: userMessage
      })
    );

    setInputValue("");

    try {
      const result = await dispatch(fetchAiSearchParts(userMessage)).unwrap();
      setQuery(result?.query);

      if (result.success) {
        // Check if we have vehicle data
        const hasVehicleData = result.vin && result?.vehicleDetails?.length > 0;
        const hasResults = result.results && result.results.length > 0;

        if (hasResults && result.aiResponse) {
          // Has parts and AI response
          dispatch(
            addMessage({
              sender: "assistant",
              type: "text",
              text: result.aiResponse
            })
          );

          dispatch(
            addMessage({
              sender: "assistant",
              type: "search_results_link",
              text: `I found ${result.totalResults} part${
                result.totalResults !== 1 ? "s" : ""
              } matching your query.`,
              data: {
                results: result.results,
                query: result.query,
                totalResults: result.totalResults,
                vehicleData: hasVehicleData ? result.vehicleDetails : null
              }
            })
          );
        } else if (hasVehicleData && !hasResults) {
          // Has vehicle data but no parts found
          dispatch(
            addMessage({
              sender: "assistant",
              type: "vehicle_data_no_parts",
              text: "I found vehicle information for your search, but no matching parts are currently available.",
              data: {
                query: result.query,
                vehicleData: result.vehicleDetails,
                results: []
              }
            })
          );
        } else if (
          result.aiResponse &&
          (!result.results || result.results.length === 0)
        ) {
          // AI response but no results
          dispatch(
            addMessage({
              sender: "assistant",
              type: "text",
              text: result.aiResponse
            })
          );
        } else if (
          result.results &&
          result.results.length > 0 &&
          !result.aiResponse
        ) {
          // Has results but no AI response
          dispatch(
            addMessage({
              sender: "assistant",
              type: "search_results_link",
              text: `I found ${result.totalResults} part${
                result.totalResults !== 1 ? "s" : ""
              } matching your query.`,
              data: {
                results: result.results,
                query: result.query,
                totalResults: result.totalResults,
                vehicleData: hasVehicleData ? result.vehicleDetails : null
              }
            })
          );
        } else {
          let messageText = "";
          if (user && user?.role === "seller") {
            messageText =
              "I couldn't find specific parts matching your query. You can add this part to your inventory — it helps buyers if they are looking for the same or submit a part inquiry our sellers might have what you're looking for.";
          } else {
            messageText =
              "I couldn't find specific parts matching your query. Would you like to submit a part inquiry? Our sellers might have what you're looking for.";
          }
          dispatch(
            addMessage({
              sender: "assistant",
              type: "inquiry_form_prompt",
              text: messageText
            })
          );
        }
      } else {
        dispatch(
          addMessage({
            sender: "assistant",
            type: "text",
            text: "Invalid query. Please try again with a different search term or be more specific."
          })
        );
      }
    } catch (error) {
      console.error("Error during AI search:", error);
      dispatch(
        addMessage({
          sender: "assistant",
          type: "text",
          text: "Sorry, I encountered an error processing your request. Please try again."
        })
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleViewResults = async (data) => {
    if (!data) return;

    try {
      let enrichedResults = [];

      if (Array.isArray(data?.results) && data.results.length > 0) {
        enrichedResults = await Promise.all(
          data.results.map(async (item) => {
            let sellerInfo = null;
            let partInfo = null;

            if (item?.sellerId) {
              try {
                sellerInfo = await dispatch(
                  fetchSellerById({ userId: item.sellerId })
                ).unwrap();
              } catch (err) {
                console.error("Failed to fetch seller:", err);
              }
            }

            if (item?.objectID) {
              try {
                partInfo = await dispatch(
                  fetchPartById(item.objectID)
                ).unwrap();
              } catch (err) {
                console.error("Failed to fetch part:", err);
              }
            }

            return {
              ...item,
              sellerInfo,
              partInfo
            };
          })
        );
      }

      navigate("/ai-search-results", {
        state: {
          query: data.query,
          results: enrichedResults,
          aiResponse: aiResponse,
          totalResults: data.totalResults || enrichedResults.length,
          vehicleData: data.vehicleData || null
        }
      });

      onClose();
    } catch (err) {
      console.error("Error enriching results:", err);
    }
  };

  const handleInquiryClick = () => {
    navigate("/inquire-part", {
      state: { query }
    });
    onClose();
  };

  const handleAddInventoryClick = () => {
    navigate("/dashboard/add-part");
    onClose();
  };

  useEffect(() => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]"
      );
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }
  }, [messages]);

  const MessageContent = ({ msg }) => {
    if (msg.type === "text") {
      return <p className="text-sm whitespace-pre-wrap">{msg.text}</p>;
    }

    if (msg.type === "search_results_link" && msg.data) {
      return (
        <div>
          <p className="text-sm whitespace-pre-wrap mb-3">{msg.text}</p>
          <Button
            onClick={() => handleViewResults(msg.data)}
            variant="outline"
            size="sm"
            className="w-full"
          >
            See Matching Parts <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      );
    }

    if (msg.type === "vehicle_data_no_parts" && msg.data) {
      const vehicleInfo = msg.data.vehicleData
        ?.filter((item) =>
          ["Make", "Model", "Model Year"].includes(item.Variable)
        )
        .map((item) => item.Value)
        .join(" ");

      return (
        <div className="flex flex-col space-y-5">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-500/20 to-blue-500/40 text-blue-500 shadow-sm">
                <Info className="h-4 w-4" />
              </div>
            </div>

            <div className="flex-1 space-y-2">
              <p className="text-sm font-medium text-foreground">
                Vehicle Information Found
              </p>
              <p className="text-xs pb-1 text-muted-foreground">
                {vehicleInfo && (
                  <p className="text-xs text-muted-foreground mb-2">
                    {vehicleInfo}
                  </p>
                )}
              </p>
              <Button
                onClick={() => handleViewResults(msg.data)}
                size="sm"
                variant="outline"
                className="w-full"
              >
                <Info className="mr-2 h-3 w-3" />
                View Full Vehicle Details
              </Button>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-500/20 to-purple-500/40 text-purple-500 shadow-sm">
                <Info className="h-4 w-4" />
              </div>
            </div>

            <div className="flex-1 space-y-2">
              <p className="text-sm font-medium text-foreground">
                No Parts Found for Your Search
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Looks like we couldn’t find any matching parts.
                {user?.role === "seller"
                  ? " You can add one to your inventory or submit an inquiry."
                  : " You can submit a part inquiry to request it."}
              </p>

              {!user || user?.role === "buyer" ? (
                <Button
                  onClick={handleInquiryClick}
                  size="sm"
                  className="w-full bg-gradient-to-r from-primary to-accent text-white shadow hover:opacity-90 transition-all"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Submit Part Inquiry
                </Button>
              ) : user?.role === "seller" ? (
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={handleAddInventoryClick}
                    size="sm"
                    className="w-full sm:w-auto bg-gradient-to-r from-primary to-accent text-white shadow hover:opacity-90 transition-all"
                  >
                    <ListPlus className="mr-2 h-4 w-4" />
                    Add Part to Inventory
                  </Button>

                  <Button
                    onClick={handleInquiryClick}
                    size="sm"
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Submit Part Inquiry
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      );
    }

    if (msg.type === "inquiry_form_prompt") {
      return (
        <div>
          <p className="text-sm whitespace-pre-wrap mb-3">{msg.text}</p>
          {!user || user?.role === "buyer" ? (
            <Button
              onClick={handleInquiryClick}
              size="sm"
              className="w-full bg-gradient-to-r from-primary to-accent text-white shadow hover:opacity-90 transition-all"
            >
              <FileText className="mr-2 h-4 w-4" />
              Submit Part Inquiry
            </Button>
          ) : user?.role === "seller" ? (
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleAddInventoryClick}
                size="sm"
                className="w-full sm:w-auto bg-gradient-to-r from-primary to-accent text-white shadow hover:opacity-90 transition-all"
              >
                <ListPlus className="mr-2 h-4 w-4" />
                Add Part to Inventory
              </Button>

              <Button
                onClick={handleInquiryClick}
                size="sm"
                variant="outline"
                className="w-full sm:w-auto"
              >
                <FileText className="mr-2 h-4 w-4" />
                Submit Part Inquiry
              </Button>
            </div>
          ) : null}
        </div>
      );
    }

    return <p className="text-sm whitespace-pre-wrap">{msg.text || "..."}</p>;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent
            hideClose
            className="sm:max-w-[450px] md:max-w-[600px] lg:max-w-[700px] p-0 flex flex-col h-[80vh] max-h-[700px] bg-gradient-to-br from-background via-secondary/10 to-background shadow-2xl rounded-xl border-border/20 z-[9999]"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col h-full"
            >
              <DialogHeader className="p-6 border-b border-border/20">
                <div className="flex justify-between items-center">
                  <DialogTitle className="text-xl sm:text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-500 to-accent">
                    Kato – PartsLokator AI
                  </DialogTitle>
                  <DialogClose asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full p-0 hover:bg-muted transition"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </DialogClose>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Intelligent assistance to quickly locate the right parts.
                </p>
              </DialogHeader>

              <ScrollArea className="flex-grow p-6" ref={scrollAreaRef}>
                <div className="space-y-6">
                  {(messages || []).map((msg, index) => (
                    <motion.div
                      key={msg.id || `${msg.timestamp}-${index}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.04 }}
                      className={`flex items-start gap-3 ${
                        msg.sender === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      {msg.sender === "assistant" && (
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 flex items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/40 text-primary shadow-sm">
                            <Bot className="h-5 w-5" />
                          </div>
                        </div>
                      )}

                      <div
                        className={`group relative max-w-[70%] px-4 py-3 rounded-2xl shadow-sm transition-all ${
                          msg.sender === "user"
                            ? "bg-gradient-to-r from-primary to-primary/80 text-white rounded-br-sm hover:shadow-md"
                            : "bg-muted text-muted-foreground rounded-bl-sm hover:shadow-md"
                        }`}
                      >
                        <MessageContent msg={msg} />

                        <span className="absolute -bottom-5 right-2 text-[11px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                          {new Date(msg.timestamp).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </span>
                      </div>

                      {msg.sender === "user" && (
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 flex items-center justify-center rounded-full bg-muted text-muted-foreground shadow-sm">
                            <User className="h-5 w-5" />
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}

                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-end gap-3 justify-start"
                    >
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 flex items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/40 text-primary shadow-sm animate-pulse">
                          <Bot className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="px-4 py-3 rounded-2xl shadow-sm bg-muted text-muted-foreground rounded-bl-sm">
                        <div className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce"></span>
                          <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce delay-150"></span>
                          <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce delay-300"></span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </ScrollArea>

              <DialogFooter className="p-4 border-t border-border/20 bg-background/95">
                <div className="flex w-full items-center gap-3">
                  <Input
                    type="text"
                    placeholder="Ask Kato to find the right part..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    className="flex-grow h-12 rounded-full border border-border/30 bg-muted/40 px-5 shadow-sm focus:border-primary focus:ring-1 focus:ring-primary transition text-sm sm:text-base"
                    disabled={isLoading}
                  />

                  <Button
                    onClick={handleSend}
                    disabled={isLoading || !inputValue.trim()}
                    className="h-12 w-12 flex items-center justify-center rounded-full bg-gradient-to-r from-primary to-accent text-white shadow-md hover:scale-105 hover:shadow-lg transition-all"
                    aria-label="Send"
                  >
                    <Send className="h-5 w-5" />
                  </Button>
                </div>
              </DialogFooter>
            </motion.div>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default AiAssistantChatDialog;
